import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { WelcomeDialog } from '@/components/WelcomeDialog';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isTabRecovering: boolean;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: { full_name?: string }) => Promise<void>;
  showAuthModal: (tab?: 'login' | 'register') => void;
  hideAuthModal: () => void;
  isAuthModalOpen: boolean;
  authModalTab: 'login' | 'register';
  showWelcomeMessage: boolean;
  hideWelcomeMessage: () => void;
  retryAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isTabRecovering, setIsTabRecovering] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('login');
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Initialer Auth-Status
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing auth...');
        
        // Timeout für die Session-Abfrage
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session timeout')), 20000)
        );
        
        try {
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as any;
        
        if (error) {
          console.error('❌ Error getting session:', error);
          throw error;
        }
        
        console.log('📋 Current session:', session ? 'Found' : 'None');
        
        if (mounted) {
          if (session?.user) {
            console.log('✅ User found in session:', session.user.email);
            setUser(session.user);
            await updateProfile(session.user);
          } else {
            console.log('❌ No user in session');
            setUser(null);
          }
          setLoading(false);
          }
        } catch (raceError) {
          console.error('❌ Session initialization error:', raceError);
          // Bei einem Timeout versuchen wir es ohne Race Condition
          if (raceError.message && raceError.message.includes('Session timeout')) {
            console.log('⏱️ Session timeout - trying direct approach');
            try {
              const { data } = await supabase.auth.getSession();
              if (mounted) {
                if (data.session?.user) {
                  console.log('✅ User found after timeout recovery:', data.session.user.email);
                  setUser(data.session.user);
                  await updateProfile(data.session.user);
                } else {
                  console.log('❌ No user in session after timeout recovery');
                  setUser(null);
                }
                setLoading(false);
              }
            } catch (directError) {
              console.error('❌ Direct session approach failed:', directError);
              if (mounted) {
                setUser(null);
                setLoading(false);
              }
            }
          } else {
            // Andere Fehler
            if (mounted) {
              setUser(null);
              setLoading(false);
            }
          }
        }

        // Setze den Auth State Change Listener mit bewährter setTimeout-Lösung
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          console.log('🔄 Auth state changed:', event, session?.user?.email || 'No user');
          
          // Verwende setTimeout um Deadlocks zu vermeiden (bewährte Lösung)
          setTimeout(async () => {
            if (!mounted) return;
            
            // Prüfe ob Session tatsächlich geändert wurde (verhindert unnötige Re-Renders)
            const currentUserId = user?.id;
            const newUserId = session?.user?.id;
            const sessionExpiry = session?.expires_at;
            
            // Speichere die aktuelle Session-Expiry um unnötige Updates zu vermeiden
            const lastExpiry = localStorage.getItem('lastSessionExpiry');
            
            if (session?.user) {
              // Nur aktualisieren wenn sich User ID geändert hat oder neue Session
              if (currentUserId !== newUserId || !currentUserId) {
              setUser(session.user);
              await updateProfile(session.user);
              
              // Prüfe ob es sich um eine E-Mail-Bestätigung handelt
              if (event === 'SIGNED_IN' && window.location.hash.includes('type=signup')) {
                setShowWelcomeMessage(true);
                // Entferne Hash aus URL
                window.history.replaceState(null, '', window.location.pathname);
                }
              }
              
              // Speichere Session-Expiry für spätere Vergleiche
              if (sessionExpiry) {
                localStorage.setItem('lastSessionExpiry', sessionExpiry.toString());
              }
            } else {
              // Nur auf null setzen wenn wir tatsächlich einen User hatten
              if (currentUserId) {
              setUser(null);
                localStorage.removeItem('lastSessionExpiry');
                localStorage.removeItem('lastSessionCheck');
              }
            }
            setLoading(false);
          }, 0); // Ausführung in der nächsten Event Loop
        });

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        if (mounted) {
          setUser(null);
          setLoading(false);
          // Nur bei echten Fehlern eine Toast anzeigen, nicht bei fehlenden Sessions
          if (error.message && 
              !error.message.includes('session_not_found') && 
              !error.message.includes('Session timeout')) {
          toast.error('Fehler bei der Authentifizierung');
        }
        }
      }
    };

    // Setze einen zusätzlichen Timeout als Fallback
    const fallbackTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.log('⏰ Auth initialization timeout - forcing loading to false');
        setLoading(false);
      }
    }, 15000); // 15 Sekunden als absoluter Fallback

    initializeAuth();

    return () => {
      clearTimeout(fallbackTimeout);
    };
  }, []);

  // Session-Recovery bei Window-Fokus und Tab-Wechsel
  useEffect(() => {
    let tabRecoveryTimeout: NodeJS.Timeout;

    const handleFocus = async () => {
      if (!user && !loading) {
        console.log('🔍 Window focused, checking for session...');
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            console.log('🔄 Session recovered on focus');
            setUser(session.user);
            await updateProfile(session.user);
          }
        } catch (error) {
          console.error('❌ Error recovering session on focus:', error);
        }
      }
    };

    // Optimierte Tab-Wechsel-Behandlung ohne aggressive Session-Checks
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Tab wurde versteckt - keine Aktion nötig
        return;
      }

      // Tab wurde wieder sichtbar - nur bei tatsächlich notwendigen Checks
      if (user && !isTabRecovering) {
        // Prüfe nur, wenn der letzte Check länger als 5 Minuten her ist
        const lastCheck = localStorage.getItem('lastSessionCheck');
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
          
        if (lastCheck && (now - parseInt(lastCheck)) < fiveMinutes) {
          console.log('✅ Session check nicht nötig - kürzlich überprüft');
          return;
        }

        console.log('🔍 Tab became visible, quick session check...');
        setIsTabRecovering(true);

        // Timeout für Recovery-State (reduziert auf 3 Sekunden)
        tabRecoveryTimeout = setTimeout(() => {
          setIsTabRecovering(false);
        }, 3000);

        try {
          // Verwende die in onAuthStateChange gespeicherte Session anstatt neuer API-Calls
          const currentSession = await supabase.auth.getSession();
          
          // Speichere Zeitstempel des letzten Checks
          localStorage.setItem('lastSessionCheck', now.toString());

          // Nur bei tatsächlichen Problemen handeln
          if (currentSession.error) {
            console.warn('Session-Fehler erkannt:', currentSession.error);
            // Nur bei schwerwiegenden Fehlern Token refreshen
            if (currentSession.error.message?.includes('refresh_token_not_found') || 
                currentSession.error.message?.includes('invalid_grant')) {
              console.log('🚪 Kritischer Auth-Fehler - Session beenden');
                await signOut();
              }
          } else if (currentSession.data.session?.user) {
            console.log('✅ Session ist aktiv');
          }
        } catch (error) {
          console.error('❌ Minimaler Session-Check fehlgeschlagen:', error);
          // Keine weiteren Aktionen bei Fehlern
        } finally {
          setIsTabRecovering(false);
          if (tabRecoveryTimeout) {
            clearTimeout(tabRecoveryTimeout);
          }
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (tabRecoveryTimeout) {
        clearTimeout(tabRecoveryTimeout);
      }
    };
  }, [user, loading, isTabRecovering]);

  // Reduzierte automatische Session-Überwachung
  useEffect(() => {
    if (!user) return;

    // Prüfe Session alle 15 Minuten (reduziert für weniger Server-Load)
    const sessionCheckInterval = setInterval(async () => {
      try {
        // Nur prüfen wenn Tab aktiv ist
        if (document.hidden) {
          console.log('🔍 Tab ist versteckt - überspringe Session-Check');
          return;
        }

        console.log('🔍 Automatische Session-Prüfung...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.warn('❌ Session-Prüfung fehlgeschlagen:', error);
          // Nur bei kritischen Fehlern handeln
          if (error.message?.includes('refresh_token_not_found') || 
              error.message?.includes('invalid_grant')) {
              console.log('🚪 Kritischer Auth-Fehler - Benutzer wird abgemeldet');
              await signOut();
          }
        } else if (!session?.user) {
          console.warn('❌ Keine gültige Session gefunden');
          setUser(null);
        } else {
          console.log('✅ Session ist gültig');
        }
      } catch (error) {
        console.error('❌ Fehler bei automatischer Session-Prüfung:', error);
        // Keine weitere Aktion bei Fehlern um Endlos-Loops zu vermeiden
      }
    }, 15 * 60 * 1000); // Alle 15 Minuten

    return () => clearInterval(sessionCheckInterval);
  }, [user]);

  const updateProfile = async (currentUser: User) => {
    try {
      console.log('📝 Updating profile for user:', currentUser.email);
      
      // Prüfen, ob der Benutzer bereits ein benutzerdefiniertes Profil hat
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('custom_full_name, full_name')
        .eq('id', currentUser.id)
        .maybeSingle();
      
      if (fetchError) {
        console.error('❌ Error fetching existing profile:', fetchError);
      }
      
      // Wenn der Benutzer einen benutzerdefinierten Namen hat, diesen IMMER beibehalten
      const hasCustomName = existingProfile?.custom_full_name === true;
      let fullName = '';
      
      if (hasCustomName && existingProfile?.full_name) {
        // Benutzerdefinierten Namen beibehalten
        fullName = existingProfile.full_name;
        console.log('👤 Keeping custom name:', fullName);
      } else {
        // Provider-Namen verwenden (Google, etc.) oder E-Mail-Fallback
        fullName = currentUser.user_metadata.full_name || 
                   currentUser.user_metadata.name || 
                   currentUser.email?.split('@')[0] || 
                   '';
        console.log('👤 Using provider/email name:', fullName);
      }
      
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: currentUser.id,
          email: currentUser.email,
          full_name: fullName,
          updated_at: new Date().toISOString(),
          // Beibehalten des custom_full_name Flags, falls es existiert
          custom_full_name: hasCustomName || false
        }, {
          onConflict: 'id'
        });

      if (profileError) {
        console.error('❌ Error updating profile in database:', profileError);
        // Nicht blockierend - Profil-Update-Fehler sollten die Auth nicht stoppen
        return;
      }

      console.log('✅ Profile updated successfully with name:', fullName);
    } catch (error) {
      console.error('❌ Error in updateProfile:', error);
      // Nicht blockierend - Profil-Update-Fehler sollten die Auth nicht stoppen
    }
  };

  const updateUserProfile = async (updates: { full_name?: string }) => {
    try {
      if (!user) {
        toast.error('Kein Benutzer angemeldet');
        return;
      }

      console.log('🔄 Updating user profile with:', updates);

      // Update user metadata (nur full_name)
      const { data: userData, error: userError } = await supabase.auth.updateUser({
        data: { full_name: updates.full_name }
      });

      if (userError) throw userError;

      // Update profiles table (nur full_name)
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: updates.full_name || user.user_metadata.full_name || '',
          email: user.email, // E-Mail bleibt unverändert
          updated_at: new Date().toISOString(),
          // Immer auf true setzen, um den benutzerdefinierten Namen zu sichern
          custom_full_name: true
        }, {
          onConflict: 'id'
        });

      if (profileError) throw profileError;

      // Update local user state mit den neuen Daten
      if (userData.user) {
        console.log('✅ User profile updated successfully, new name:', updates.full_name);
        setUser(userData.user);
        toast.success('Profil erfolgreich aktualisiert');
      }
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      toast.error('Fehler beim Aktualisieren des Profils');
    }
  };

  const signOut = async () => {
    try {
      console.log('🚪 Signing out user...');
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Error during signOut:', error);
        throw error;
      }
      
      // Explizit den User-State zurücksetzen
      setUser(null);
      setIsAuthModalOpen(false);
      setShowWelcomeMessage(false);
      
      console.log('✅ User signed out successfully');
      toast.success('Erfolgreich abgemeldet');
    } catch (error) {
      console.error('❌ Error signing out:', error);
      // Auch bei Fehlern den lokalen State zurücksetzen
      setUser(null);
      toast.error('Fehler beim Abmelden');
    }
  };

  const showAuthModal = (tab: 'login' | 'register' = 'login') => {
    // Speichere aktuelle Scroll-Position vor dem Öffnen
    const scrollY = window.scrollY;
    localStorage.setItem('scrollPositionBeforeModal', scrollY.toString());
    
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  };

  const hideAuthModal = () => {
    setIsAuthModalOpen(false);
    
    // Stelle Scroll-Position nach dem Schließen wieder her
    setTimeout(() => {
      const savedScrollY = localStorage.getItem('scrollPositionBeforeModal');
      if (savedScrollY) {
        window.scrollTo(0, parseInt(savedScrollY));
        localStorage.removeItem('scrollPositionBeforeModal');
      }
    }, 100); // Kurze Verzögerung damit das Modal vollständig geschlossen ist
  };

  const hideWelcomeMessage = () => {
    setShowWelcomeMessage(false);
  };

  const retryAuth = async () => {
    console.log('🔄 Retrying authentication...');
    setLoading(true);
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) throw error;
      
      if (session?.user) {
        console.log('✅ User found in retry:', session.user.email);
        setUser(session.user);
        await updateProfile(session.user);
      } else {
        console.log('❌ No user found in retry');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Error in retry auth:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isTabRecovering,
      signOut, 
      updateUserProfile,
      showAuthModal,
      hideAuthModal,
      isAuthModalOpen,
      authModalTab,
      showWelcomeMessage,
      hideWelcomeMessage,
      retryAuth
    }}>
      {children}
      <WelcomeDialog
        isOpen={showWelcomeMessage}
        onClose={hideWelcomeMessage}
        userName={user?.user_metadata?.full_name}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 